import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const euro = v => v != null ? Number(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : 'Nicht bekannt';
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('de-DE') : '';

const KSTATUS = {
  vorgemerkt:  { label: 'Vorgemerkt',          color: '#a16207', bg: '#fffbeb' },
  vorbereitet: { label: 'Vorbereitet',          color: '#2563eb', bg: '#eff6ff' },
  versendet:   { label: 'Versendet',            color: '#7c3aed', bg: '#f5f3ff' },
  bestaetigt:  { label: 'Bestätigt / Erledigt', color: '#0a6f4f', bg: '#ecfdf5' },
};

const card = { background: '#fff', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };
const h2s = { margin: 0, color: '#064734', fontSize: '17px' };
function btn(bg, small) {
  return { background: bg, color: '#fff', border: 'none', borderRadius: '8px', padding: small ? '7px 10px' : '10px 14px', cursor: 'pointer', fontWeight: 900, fontSize: small ? '12px' : '14px' };
}

function StatusBadge({ kstatus }) {
  const cfg = KSTATUS[kstatus] || KSTATUS.vorgemerkt;
  return <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33`, borderRadius: '999px', padding: '3px 9px', fontSize: '11px', fontWeight: 900 }}>{cfg.label}</span>;
}

function Notice({ tone, children }) {
  const red = tone === 'red';
  return <div style={{ background: red ? '#fff1ef' : '#dff3e9', color: red ? '#9f1d12' : '#064734', border: `1px solid ${red ? '#ffc9c2' : '#a7d9c2'}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

function buildLetterText(f) {
  const vertragRef = f.vertrags_nr
    ? `Vertragsnummer: ${f.vertrags_nr}`
    : f.kunden_nr
      ? `Kundennummer: ${f.kunden_nr}`
      : '[Bitte Vertragsnummer / Kundennummer ergänzen]';
  const terminText = f.terminArt === 'datum' && f.terminDatum
    ? `zum ${fmtDate(f.terminDatum)}`
    : 'zum nächstmöglichen Zeitpunkt';
  return `Sehr geehrte Damen und Herren,

hiermit kündige ich den mit Ihnen bestehenden Vertrag (${vertragRef}) ${terminText}, fristgerecht und außerordentlich, hilfsweise zum nächstmöglichen Termin.

Bitte bestätigen Sie mir den Eingang dieser Kündigung sowie das Datum der Vertragsbeendigung schriftlich.

Mit freundlichen Grüßen

${f.absender || '[Name]'}`;
}

// ── Letter View ───────────────────────────────────────────────────────────────

function LetterView({ item, onBack, onSave }) {
  const [f, setF] = useState({
    absender: '',
    anschrift: '',
    vertrags_nr: item.vertrags_nr || '',
    kunden_nr: item.kunden_nr || '',
    terminArt: 'naechstmoeglich',
    terminDatum: '',
    betreff: `Kündigung meines Vertrags bei ${item.empfaenger}`,
    notiz: item.kuendigung_notiz || '',
  });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [showVersendet, setShowVersendet] = useState(false);
  const [vWeg, setVWeg] = useState('');
  const [vDatum, setVDatum] = useState(today());

  const letterText = buildLetterText(f);

  const inp = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '8px 10px', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '14px' };
  const label = { display: 'block', fontWeight: 700, color: '#334155', fontSize: '12px', marginBottom: '4px' };
  const field = (key, placeholder, type='text', rows=null) => rows
    ? <textarea rows={rows} style={{ ...inp, resize: 'vertical' }} value={f[key]} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} />
    : <input type={type} style={inp} value={f[key]} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} />;

  async function markAs(kstatus, extra = {}) {
    setStatusBusy(true);
    await onSave(item, kstatus, {
      vertrags_nr: f.vertrags_nr || null,
      kunden_nr: f.kunden_nr || null,
      notiz: f.notiz || null,
      ...extra
    });
    setStatusBusy(false);
  }

  async function copyText() {
    try {
      await navigator.clipboard.writeText([
        f.absender, '', fmtDate(today()), '', item.empfaenger,
        f.anschrift || '[Anschrift des Anbieters]', '', `Betreff: ${f.betreff}`, '',
        letterText
      ].join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { alert('Kopieren fehlgeschlagen. Bitte manuell markieren.'); }
  }

  function printLetter() {
    const win = window.open('', '_blank');
    const anschrift = f.anschrift || '[Anschrift des Anbieters hier ergänzen]';
    win.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8">
<title>Kündigung ${item.empfaenger}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12pt; color: #000; margin: 0; padding: 0; }
  .page { width: 210mm; min-height: 297mm; padding: 25mm 25mm 20mm 30mm; box-sizing: border-box; }
  .sender { margin-bottom: 20mm; font-size: 10pt; }
  .recipient { margin-bottom: 10mm; }
  .date-line { text-align: right; margin-bottom: 8mm; }
  .subject { font-weight: bold; margin: 6mm 0; }
  .body { line-height: 1.6; white-space: pre-line; }
  @media print { @page { size: A4; margin: 0; } body { -webkit-print-color-adjust: exact; } }
</style></head><body>
<div class="page">
  <div class="sender">${(f.absender || '[Ihr Name]').replace(/\n/g, '<br>')}</div>
  <div class="recipient">${item.empfaenger}<br>${anschrift.replace(/\n/g, '<br>')}</div>
  <div class="date-line">${fmtDate(today())}</div>
  <div class="subject">Betreff: ${f.betreff}</div>
  <div class="body">${letterText.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</div>
</div>
</body></html>`);
    win.document.close();
    win.print();
  }

  const jahreskosten = item.betrag ? euro(item.betrag * 12) : null;

  return (
    <div>
      <button onClick={onBack} style={{ ...btn('#334155', true), marginBottom: '16px' }}>← Zurück</button>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '16px', alignItems: 'start' }}>

        {/* Left: Details + Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={card}>
            <h2 style={{ ...h2s, marginBottom: '8px' }}>Vertragsdaten</h2>
            <div style={{ fontWeight: 800, fontSize: '18px', color: '#12231d' }}>{item.empfaenger}</div>
            <div style={muted}>{item.kategorie} · {item.quelle === 'radar' ? 'Vertragsradar' : 'Geldfresser-Analyse'}</div>
            {item.betrag && <div style={{ marginTop: '8px', color: '#0a6f4f', fontWeight: 900 }}>{euro(item.betrag)} / Monat {jahreskosten && <span style={muted}>· {jahreskosten} / Jahr</span>}</div>}
            <div style={{ marginTop: '8px' }}><StatusBadge kstatus={item.kstatus} /></div>
          </div>

          <div style={card}>
            <h2 style={{ ...h2s, marginBottom: '12px' }}>Kündigungsschreiben</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div><span style={label}>Ihr Name / Absender (mehrzeilig möglich)</span>{field('absender', 'Max Mustermann\nMusterstraße 1\n12345 Musterstadt', 'text', 3)}</div>
              <div><span style={label}>Anschrift des Anbieters (oder leer lassen)</span>{field('anschrift', 'Anschrift nicht bekannt', 'text', 2)}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div><span style={label}>Vertragsnummer</span>{field('vertrags_nr', 'Nicht bekannt')}</div>
                <div><span style={label}>Kundennummer</span>{field('kunden_nr', 'Nicht bekannt')}</div>
              </div>
              <div>
                <span style={label}>Kündigungstermin</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ display: 'flex', gap: '5px', alignItems: 'center', fontWeight: 700, fontSize: '13px' }}>
                    <input type="radio" checked={f.terminArt === 'naechstmoeglich'} onChange={() => setF(p => ({ ...p, terminArt: 'naechstmoeglich' }))} />
                    Zum nächstmöglichen Zeitpunkt
                  </label>
                  <label style={{ display: 'flex', gap: '5px', alignItems: 'center', fontWeight: 700, fontSize: '13px' }}>
                    <input type="radio" checked={f.terminArt === 'datum'} onChange={() => setF(p => ({ ...p, terminArt: 'datum' }))} />
                    Zum Datum:
                  </label>
                  {f.terminArt === 'datum' && <input type="date" style={{ ...inp, width: '140px' }} value={f.terminDatum} onChange={e => setF(p => ({ ...p, terminDatum: e.target.value }))} />}
                </div>
              </div>
              <div><span style={label}>Betreff</span>{field('betreff', 'Kündigung')}</div>
              <div><span style={label}>Interne Notiz (wird nicht gedruckt)</span>{field('notiz', 'Gesprächsnotizen, Fristen...', 'text', 2)}</div>
            </div>
          </div>

          <div style={card}>
            <h2 style={{ ...h2s, marginBottom: '10px' }}>Kündigungsstatus</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              <button disabled={statusBusy} onClick={() => markAs('vorbereitet')} style={btn('#2563eb', true)}>Als vorbereitet markieren</button>
              <button disabled={statusBusy} onClick={() => setShowVersendet(true)} style={btn('#7c3aed', true)}>Als versendet markieren</button>
              <button disabled={statusBusy} onClick={() => markAs('bestaetigt')} style={btn('#0a6f4f', true)}>Kündigung bestätigt</button>
            </div>
            {showVersendet && (
              <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div><span style={label}>Versanddatum</span><input type="date" style={inp} value={vDatum} onChange={e => setVDatum(e.target.value)} /></div>
                  <div><span style={label}>Versandweg</span>
                    <select style={inp} value={vWeg} onChange={e => setVWeg(e.target.value)}>
                      <option value="">Bitte wählen</option>
                      <option>E-Mail</option><option>Brief</option><option>Anbieter-Website</option><option>App</option><option>Sonstiges</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button disabled={statusBusy || !vWeg} onClick={() => { markAs('versendet', { versendet_am: vDatum, versandweg: vWeg }); setShowVersendet(false); }} style={btn('#7c3aed', true)}>Speichern</button>
                  <button onClick={() => setShowVersendet(false)} style={btn('#94a3b8', true)}>Abbrechen</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Letter Preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ ...card, background: '#fafffe' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={h2s}>Entwurf Kündigungsschreiben</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={copyText} style={btn(copied ? '#0a6f4f' : '#334155', true)}>{copied ? '✓ Kopiert' : 'Text kopieren'}</button>
                <button onClick={printLetter} style={btn('#b42318', true)}>Drucken / als PDF sichern</button>
              </div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '13px', lineHeight: 1.7, minHeight: '400px' }}>
              <div style={{ marginBottom: '16px', whiteSpace: 'pre-line' }}>{f.absender || '[Ihr Name und Anschrift]'}</div>
              <div style={{ marginBottom: '12px' }}>{item.empfaenger}<br />{f.anschrift || '[Anschrift des Anbieters]'}</div>
              <div style={{ textAlign: 'right', marginBottom: '12px' }}>{fmtDate(today())}</div>
              <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>Betreff: {f.betreff}</div>
              <div style={{ whiteSpace: 'pre-line' }}>{letterText}</div>
            </div>
            <p style={{ ...muted, marginTop: '8px' }}>Hinweis: Dies ist ein Entwurf. Keine Kündigung wird automatisch versendet. Bitte alle Angaben prüfen, insbesondere Vertragsnummer und Anschrift des Anbieters.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function KuendigungsPage() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [letter, setLetter] = useState(null);
  const [saved, setSaved] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api.get('/kuendigungen');
      setItems(r.data.items || []);
    } catch (e) {
      setError('Kündigungen konnten nicht geladen werden');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(item, kstatus, extra = {}) {
    try {
      await api.put('/kuendigungen', {
        empfaenger: item.empfaenger,
        quelle: item.quelle,
        kategorie: item.kategorie,
        kstatus,
        ...extra
      });
      setSaved(`${item.empfaenger}: Status aktualisiert.`);
      await load();
    } catch (e) {
      setError('Status konnte nicht gespeichert werden');
    }
  }

  if (letter) {
    return <LetterView item={letter} onBack={() => { setLetter(null); load(); }} onSave={updateStatus} />;
  }

  const grouped = {
    vorgemerkt:  items.filter(i => i.kstatus === 'vorgemerkt'),
    vorbereitet: items.filter(i => i.kstatus === 'vorbereitet'),
    versendet:   items.filter(i => i.kstatus === 'versendet'),
    bestaetigt:  items.filter(i => i.kstatus === 'bestaetigt'),
  };
  const offen = items.filter(i => i.kstatus !== 'bestaetigt').length;
  const ersparnis = items.filter(i => i.kstatus === 'bestaetigt' && i.betrag).reduce((s, i) => s + Number(i.betrag), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: '#b42318', fontSize: '24px' }}>Kündigungszentrale</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Zur Kündigung vorgemerkte Verträge verwalten und Schreiben vorbereiten.</p>
        </div>
        <button onClick={load} style={btn('#334155', true)}>Aktualisieren</button>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {saved && <Notice tone="green">{saved}</Notice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '14px' }}>
        {[
          { label: 'Gesamt', val: items.length, hint: 'Kündigungskandidaten' },
          { label: 'Offen', val: offen, hint: 'noch nicht bestätigt' },
          { label: 'Bestätigt', val: grouped.bestaetigt.length, hint: 'Kündigung abgeschlossen' },
          { label: 'Potenzielle Ersparnis', val: grouped.bestaetigt.length ? euro(ersparnis) + ' / Mo.' : '—', hint: 'nach Vertragsende', wide: true },
        ].map(s => (
          <div key={s.label} style={card}>
            <div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ color: '#064734', fontSize: '22px', fontWeight: 950, marginTop: '4px' }}>{s.val}</div>
            <div style={muted}>{s.hint}</div>
          </div>
        ))}
      </div>

      {busy && <div style={{ ...muted, textAlign: 'center', padding: '24px' }}>Lade Kündigungen…</div>}
      {!busy && items.length === 0 && (
        <div style={{ ...card, color: '#61726b', textAlign: 'center', padding: '32px' }}>
          Keine Kündigungskandidaten vorhanden.<br />
          Verträge im Bereich "Prüfen" (Geldfresser) oder im Vertragsradar als "Kündigen" markieren.
        </div>
      )}

      {Object.entries(grouped).filter(([, list]) => list.length > 0).map(([kstatus, list]) => (
        <section key={kstatus} style={{ marginBottom: '16px' }}>
          <h2 style={{ ...h2s, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <StatusBadge kstatus={kstatus} />
            <span style={muted}>({list.length})</span>
          </h2>
          <div style={{ display: 'grid', gap: '10px' }}>
            {list.map(item => {
              const key = `${item.empfaenger}|||${item.quelle}`;
              const open = !!expanded[key];
              const jahreskosten = item.betrag ? Number(item.betrag) * 12 : null;
              return (
                <article key={key} style={{ ...card, borderLeft: `4px solid ${KSTATUS[item.kstatus]?.color || '#a16207'}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: '16px', color: '#12231d' }}>{item.empfaenger}</div>
                      <div style={muted}>{item.kategorie} · {item.quelle === 'radar' ? 'Vertragsradar' : 'Geldfresser-Analyse'}</div>
                      {item.betrag != null && (
                        <div style={{ marginTop: '6px' }}>
                          <span style={{ color: '#b42318', fontWeight: 900 }}>{euro(item.betrag)} / Monat</span>
                          {jahreskosten && <span style={{ ...muted, marginLeft: '8px' }}>≈ {euro(jahreskosten)} / Jahr</span>}
                        </div>
                      )}
                      {item.vertrags_nr && <div style={muted}>Vertragsnr.: {item.vertrags_nr}</div>}
                      {item.versendet_am && <div style={muted}>Versendet: {fmtDate(item.versendet_am)} per {item.versandweg}</div>}
                      {item.vertragsende && <div style={{ color: '#0a6f4f', fontSize: '13px', fontWeight: 700 }}>Vertragsende: {fmtDate(item.vertragsende)}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button onClick={() => setLetter(item)} style={btn('#b42318', true)}>Kündigung vorbereiten</button>
                      <button onClick={() => setExpanded(p => ({ ...p, [key]: !p[key] }))} style={btn('#334155', true)}>{open ? '▲' : '▼'}</button>
                    </div>
                  </div>
                  {open && (
                    <div style={{ marginTop: '10px', borderTop: '1px solid #eef4f1', paddingTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {kstatus !== 'vorbereitet' && <button disabled={busy} onClick={() => updateStatus(item, 'vorbereitet')} style={btn('#2563eb', true)}>Vorbereitet</button>}
                      {kstatus !== 'versendet'   && <button disabled={busy} onClick={() => setLetter(item)} style={btn('#7c3aed', true)}>Als versendet markieren</button>}
                      {kstatus !== 'bestaetigt'  && <button disabled={busy} onClick={() => updateStatus(item, 'bestaetigt')} style={btn('#0a6f4f', true)}>Bestätigt / Erledigt</button>}
                      {kstatus === 'bestaetigt' && item.betrag && (
                        <div style={{ ...muted, padding: '4px 0' }}>
                          Monatliche Ersparnis: <strong>{euro(item.betrag)}</strong> · Jährliche Ersparnis: <strong>{euro(jahreskosten)}</strong>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
